import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { useAuth } from '../../Contexts/AuthContext';
import logo from '../../Images/Logos/f4f_logo.png';
import dfsLogo from '../../Images/Logos/dfs-logo-no-bkg.png';
import gizLogo from '../../Images/Logos/giz-logo.png';
import rssLogo from '../../Images/Logos/logoRssNormal.png';
import useTranslation from '../Language/useTranslation';

const LoginPage: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isFirefox, setIsFirefox] = useState(false);
    const { login } = useAuth(); // Use the login method from your Auth context
    const theme = useTheme();
    const t = useTranslation();

    useEffect(() => {
        const userAgent = navigator.userAgent.toLowerCase();
        if (userAgent.includes('firefox')) {
            setIsFirefox(true);
        }
    }, []);

    const handleFormSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            await login(username, password); // Call the API for login
        } catch (err) {
            setError('Login failed. Please check your credentials.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setIsFirefox(false);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: '#f4f4f4',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 400,
                    padding: 4,
                    backgroundColor: '#fff',
                    boxShadow: 3,
                    borderRadius: 2,
                }}
                component="form"
                onSubmit={handleFormSubmit}
            >
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <img src={logo} alt="Logo" style={{ maxWidth: '100%', height: 'auto' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, textAlign: 'center', mb: 2 }}>
                    Monitoring Platform
                </Typography>
                <TextField
                    autoFocus
                    margin="dense"
                    label="User Name"
                    type="text"
                    fullWidth
                    variant="outlined"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isLoading}
                    InputProps={{
                        style: { color: 'black', backgroundColor: 'white' }, // Ensure text is black and background is white
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: '#ccc', // Ensure border is visible
                            },
                            '&:hover fieldset': {
                                borderColor: '#ccc', // Ensure border is visible on hover
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#ccc', // Ensure border is visible when focused
                            },
                        },
                    }}
                />
                <TextField
                    margin="dense"
                    label="Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    InputProps={{
                        style: { color: 'black', backgroundColor: 'white' }, // Ensure text is black and background is white
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: '#ccc', // Ensure border is visible
                            },
                            '&:hover fieldset': {
                                borderColor: '#ccc', // Ensure border is visible on hover
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#ccc', // Ensure border is visible when focused
                            },
                        },
                    }}
                />
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <CircularProgress />
                    </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={isLoading}
                        fullWidth
                    >
                        Login
                    </Button>
                </Box>
            </Box>
            <Box sx={{ width: '100%', maxWidth: 400, ml: 3, mb: 3 }}>
                <Typography variant="body2" sx={{ mt: 4, mb: 2, textAlign: 'left', color: "black", fontWeight: 'bold' }}>
                    Powered by:
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <img src={gizLogo} alt="GIZ Logo" style={{ maxHeight: '70px', margin: '0 10px' }} />
                    <img src={rssLogo} alt="RSS Logo" style={{ maxHeight: '70px', margin: '0 20px' }} />
                    <img src={dfsLogo} alt="DFS Logo" style={{ maxHeight: '70px', margin: '0 20px' }} />
                </Box>
            </Box>

            <Dialog
                open={isFirefox}
                onClose={handleCloseDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{t.extras.browserCompatibilityHeading}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t.extras.browserCompatibilityText}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LoginPage;